/** @format */
import { createApp } from "vue";
import { createHead } from "@unhead/vue";
import App from "./App.vue";
import { createGtag } from "vue-gtag";
import { createRouter, createWebHistory } from "vue-router";
import Axios from "axios";
import Icon from "./utils/icon.vue";
// URL shapes are single-sourced (bead qbo.6): every route path below comes from
// ROUTE_PATTERNS so the sitemap, prerenders, and the API's Stripe redirect URLs
// can't silently drift from the router. Don't add path literals here — extend
// src/lib/paths.ts (src/lib/paths.spec.ts enforces this).
import { ROUTE_PATTERNS } from "@/lib/paths";
import logger from "@/utils/logger";
import { reportError } from "@/utils/errorReporting";
import { getVisitorId, captureFirstTouchAttribution } from "@/utils/events";
const app = createApp(App);
const head = createHead();

// IMPORTS
// À-la-carte Bootstrap build (bead hgd.12b): only the layers the templates use.
// Replaces `bootstrap/dist/css/bootstrap.css`, which shipped every component.
import "@/styles/bootstrap.scss";
app.component("IconComponent", Icon);

/* Layouts */
import frontLayout from "@/layouts/frontLayout.vue";

/* Eagerly-loaded routes (bead hgd.12b). Everything else below is a
 * route-level dynamic import so its code only downloads when navigated to:
 *  - Homepage: the landing route — lazy-loading it would ADD a round trip to
 *    first paint.
 *  - NotFound/Error: the failure surfaces. Error in particular is where the
 *    axios interceptor and the chunk-load guard below route to, so it must
 *    never itself depend on fetching a chunk. Both are tiny. */
import Homepage from "@/components/Homepage.vue";
import NotFoundComponent from "@/components/NotFound.vue";
import ErrorComponent from "@/components/Error.vue";

/* Lazily-loaded routes: shop flow + marketing/legal pages. */
const Collection = () => import("@/components/Collection.vue");
const Detail = () => import("@/components/Detail.vue");
const Cart = () => import("@/components/Cart.vue");
const Favorites = () => import("@/components/Favorites.vue");
const Success = () => import("@/components/cart/Success.vue");
const Failure = () => import("@/components/cart/Failure.vue");
const OrderStatus = () => import("@/components/order/OrderStatus.vue");
const About = () => import("@/components/standalone/About.vue");
const Stories = () => import("@/components/stories/Stories.vue");
const Story = () => import("@/components/stories/Story.vue");
const Support = () => import("@/components/standalone/Support.vue");
const FAQs = () => import("@/components/standalone/Faq.vue");
const Refund = () => import("@/components/standalone/Refund.vue");
const Privacy = () => import("@/components/standalone/Privacy.vue");
const Terms = () => import("@/components/standalone/Terms.vue");

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    } else {
      return { top: 0 };
    }
  },
  routes: [
    {
      path: ROUTE_PATTERNS.home,
      name: "home",
      component: Homepage,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.collection,
      name: "collection",
      component: Collection,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.detail,
      name: "detail",
      component: Detail,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.detail2,
      name: "detail2",
      component: Detail,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.cart,
      name: "cart",
      component: Cart,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.favorites,
      name: "favorites",
      component: Favorites,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.success,
      name: "success",
      component: Success,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.failure,
      name: "failure",
      component: Failure,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.orderStatus,
      name: "orderStatus",
      component: OrderStatus,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.support,
      name: "support",
      component: Support,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.about,
      name: "about",
      component: About,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.stories,
      name: "stories",
      component: Stories,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.story,
      name: "story",
      component: Story,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.faqs,
      name: "faqs",
      component: FAQs,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.refunds,
      name: "refunds",
      component: Refund,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.privacy,
      name: "privacy",
      component: Privacy,
      meta: {
        layout: frontLayout,
      },
    },
    {
      path: ROUTE_PATTERNS.terms,
      name: "terms",
      component: Terms,
      meta: {
        layout: frontLayout,
      },
    },
    {
      name: "NotFound",
      path: ROUTE_PATTERNS.NotFound,
      component: NotFoundComponent,
      meta: {
        layout: frontLayout,
      },
    },
    {
      // Reached by name (router.push({ name: "Error" })) from the axios
      // interceptor + global error handlers; give it a real path so it isn't a
      // dead duplicate of the NotFound catch-all above.
      name: "Error",
      path: ROUTE_PATTERNS.Error,
      component: ErrorComponent,
      meta: {
        layout: frontLayout,
      },
    },
  ],
});

// Stale-chunk recovery (bead hgd.12b). Routes are code-split, and a Pages
// deploy between page load and a navigation invalidates the old content-hashed
// chunk URLs — the dynamic import then rejects and the visitor would strand on
// a broken navigation. Recover by reloading ONCE at the target URL with a
// `?chunk_reload=1` marker appended:
//  - the marker in the URL — not sessionStorage — is the loop latch, so it
//    works with storage blocked, survives the reload, and cannot go stale in
//    the tab (it exists only on the recovery attempt and is stripped after a
//    successful navigation);
//  - appending it CHANGES the URL, so the assignment is a real document load
//    even for a same-URL #hash deep-link (location.href to an identical
//    fragment URL is a no-op fragment navigation);
//  - a second failure WITH the marker present settles on the (eagerly
//    imported, chunk-free) Error view instead of looping.
const CHUNK_RELOAD_PARAM = "chunk_reload";

// The latest navigation the USER attempted. A chunk rejection from a
// navigation they already superseded (started loading Cart, then clicked Home)
// must not yank the page back to the abandoned path.
let latestNavTarget = "";

// Show the global loading overlay while a lazy route chunk downloads — the
// axios interceptors below only cover API calls, so without this a slow chunk
// fetch is an unexplained pause. Delayed so instant (eager/warm-cache)
// navigations never flash the scrim.
let navLoadingTimer: ReturnType<typeof setTimeout> | undefined;
let navLoadingShown = false;
const NAV_LOADING_DELAY_MS = 150;
function settleNavLoading(): void {
  clearTimeout(navLoadingTimer);
  if (navLoadingShown) {
    navLoadingShown = false;
    stopLoading();
  }
}

router.beforeEach((to) => {
  latestNavTarget = to.fullPath;
  clearTimeout(navLoadingTimer);
  navLoadingTimer = setTimeout(() => {
    navLoadingShown = true;
    startLoading();
  }, NAV_LOADING_DELAY_MS);
});

router.onError((error, to) => {
  settleNavLoading();
  const message = String((error as Error)?.message ?? "");
  const isChunkLoadFailure =
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed");
  if (!isChunkLoadFailure) return;
  // Superseded navigation: the user already moved on — don't reload under them.
  if (to.fullPath !== latestNavTarget) return;

  const url = new URL(to.fullPath, window.location.origin);
  if (url.searchParams.has(CHUNK_RELOAD_PARAM)) {
    // Already the recovery attempt — the chunk is genuinely unfetchable.
    logger.error("Chunk load failed twice; routing to error view", { path: to.fullPath });
    void router.push({ name: "Error" });
    return;
  }
  url.searchParams.set(CHUNK_RELOAD_PARAM, "1");
  window.location.href = url.pathname + url.search + url.hash;
});

router.afterEach((to) => {
  settleNavLoading();
  // Strip the recovery marker after a successful navigation so it doesn't
  // linger in the address bar or in copied/shared links.
  if (to.query[CHUNK_RELOAD_PARAM] !== undefined) {
    const { [CHUNK_RELOAD_PARAM]: _omit, ...query } = to.query;
    void router.replace({ path: to.path, query, hash: to.hash });
  }
});

// When running locally, we don't need a baseURL because the Vite proxy handles /api paths
// Only use the direct API URL in production
if (import.meta.env.PROD) {
  Axios.defaults.baseURL = import.meta.env.VITE_VUE_APP_API as string;
}

import { startLoading, stopLoading } from "./composables/useLoading";

// Fire-and-forget telemetry endpoints. A failure on these must NEVER drive
// top-level navigation or be re-logged — logger.error routes through reportError
// (-> POST /api/client-errors), so logging a telemetry failure here would
// re-enter this interceptor and recurse into a request storm. They are also
// excluded from the loading overlay so background pings never flash a scrim.
// /api/experiments/assignments is best-effort personalization fetched at app
// level (App.vue): a failure must render the static defaults, NEVER eject the
// visitor to the Error view or flash the scrim (2026-07-04 solidity review —
// without this, a transient blip on that one endpoint error-paged EVERY route).
const TELEMETRY_PATHS = ["/api/events", "/api/client-errors", "/api/experiments/assignments"];
const isTelemetry = (url?: string) =>
  !!url && TELEMETRY_PATHS.some((p) => url.includes(p));

// Cart/checkout requests render their own inline, recoverable error UI
// (useCheckout sets errorMessage, shown in Cart.vue). They must NOT be hijacked
// by the global interceptor into a full-page Error/NotFound redirect mid-payment.
const isCheckout = (url?: string) => !!url && url.includes("/api/cart");

// Global loading state. Each in-flight request pulses startLoading()/stopLoading()
// on the useLoading composable (an in-flight counter clamped at 0), so concurrent
// requests don't hide the overlay early or wedge it open. App.vue reads isLoading.
Axios.interceptors.request.use(
  (config) => {
    if (!isTelemetry(config.url)) startLoading();
    return config;
  },
  (error) => {
    if (!isTelemetry(error.config?.url)) stopLoading();
    return Promise.reject(error);
  },
);

Axios.interceptors.response.use(
  (response) => {
    if (!isTelemetry(response.config?.url)) stopLoading();
    return response;
  },
  (error) => {
    // Background telemetry: reject silently — no overlay, no logging, no nav.
    if (isTelemetry(error.config?.url)) {
      return Promise.reject(error);
    }

    stopLoading();

    logger.error("Axios interceptor caught error", {
      error: error.message,
      url: error.config?.url,
    });

    // Let checkout handle its own errors inline — don't redirect away from the
    // cart mid-payment; the caller's try/catch shows a specific, recoverable msg.
    if (isCheckout(error.config?.url)) {
      return Promise.reject(error);
    }

    // Per-request opt-out: a caller that has its OWN fallback for a failed
    // request (e.g. fetchShippingOptions falls back to hardcoded speeds) sets
    // config.skipGlobalErrorRedirect so a transient failure doesn't eject the
    // user to the full-page Error view. Honour it BEFORE any redirect branch, and
    // reject so the caller's catch still runs.
    if (error.config?.skipGlobalErrorRedirect) {
      return Promise.reject(error);
    }

    try {
      if (!error.response) {
        router.push({ name: "Error" });
        throw error;
      }
      const HTTP_STATUS_CODES = {
        NOT_FOUND: 404,
        INTERNAL_SERVER_ERROR: 500,
      };

      switch (error.response.status) {
        case HTTP_STATUS_CODES.NOT_FOUND:
          router.push({ name: "NotFound" });
          break;
        case HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR:
          router.push({ name: "Error" });
          break;
        default:
          throw error;
      }
      return Promise.reject(error);
    } catch (error) {
      return Promise.reject(error);
    }
  },
);

app.use(router);
app.use(head);

// GA4 measurement ID is configured via the environment ONLY — no hardcoded
// fallback. If it's unset (e.g. local dev without a key) we skip gtag entirely
// so analytics never loads. VITE_GA_MEASUREMENT_ID is honoured as a legacy alias.
const gaMeasurementId = (import.meta.env.VITE_GA4_MEASUREMENT_ID ||
  import.meta.env.VITE_GA_MEASUREMENT_ID) as string | undefined;

// Client-side GA4. gtag.js is injected on load (vue-gtag default init mode) with
// no consent gate — the store does not front a cookie banner (owner decision:
// the audience/jurisdiction doesn't require opt-in-first). A DORMANT
// src/components/ConsentBanner.vue holds the re-enable path (manual mode +
// denied-by-default) if that ever changes (bead outstanding-wsm). Server-side
// purchase tracking and first-party /api/events funnels are independent of this.
if (gaMeasurementId) {
  app.use(createGtag({ tagId: gaMeasurementId }));
}

//Create drillable properties using provide/inject
app.provide("mediapath", import.meta.env.VITE_VUE_APP_MEDIAPATH);
app.provide("weburl", import.meta.env.VITE_VUE_APP_WEBURL);

// Stable per-browser visitor id — seeds experiment bucketing + session-level
// funnels. Generated on first access and persisted in localStorage.
app.provide("visitorId", getVisitorId());

// Capture first-touch acquisition attribution (utm_*/referrer/landing_path) on
// the very first load, before any navigation, so the originating channel is
// pinned for this browser and carried through to the eventual order.
captureFirstTouchAttribution();

// Global error observability: forward render/runtime errors and unhandled
// promise rejections to the API so the platform isn't blind to client failures.
app.config.errorHandler = (err, _instance, info): void => {
  const error = err as Error;
  logger.error("Vue errorHandler caught error", { error: error?.message, info });
  reportError({
    message: error?.message ?? String(err),
    level: "error",
    stack: error?.stack,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    context: { source: "vue.errorHandler", info },
  });
};

if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const error = reason instanceof Error ? reason : undefined;
    reportError({
      message: error?.message ?? String(reason),
      level: "error",
      stack: error?.stack,
      url: window.location.href,
      context: { source: "unhandledrejection" },
    });
  });

  window.addEventListener("error", (event) => {
    const error = event.error as Error | undefined;
    reportError({
      message: error?.message ?? event.message ?? "Unknown window error",
      level: "error",
      stack: error?.stack,
      url: window.location.href,
      context: {
        source: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
}

app.mount("#app");
